import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TableSortLabel,
  TablePagination,
  IconButton,
  Collapse,
  Link
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import axios from 'axios';
// RC Add DOMPurify import
import DOMPurify from 'dompurify';

//const API_URL = 'http://localhost:3001/api';
//For Hosting
const API_URL = '/api';
const RESULTS_PER_PAGE = 25;

interface JiraIssue {
  key: string;
  fields: {
    summary: string;
    status: {
      name: string;
    };
    priority: {
      name: string;
    };
    assignee: {
      displayName: string;
    };
    created: string;
    updated: string;
  };
}

interface ActiveFilters {
  priority?: string;
  status?: string;
}

type Order = 'asc' | 'desc';

interface HeadCell {
  id: keyof JiraIssue | 'slNo' | 'summary' | 'status' | 'priority' | 'assignee' | 'created' | 'updated';
  label: string;
  sortable: boolean;
}

const headCells: HeadCell[] = [
  { id: 'key', label: 'Bug ID', sortable: true },
  { id: 'summary', label: 'Summary', sortable: true },
  { id: 'status', label: 'Status', sortable: true },
  { id: 'priority', label: 'Priority', sortable: true },
  { id: 'assignee', label: 'Assignee', sortable: true },
  { id: 'created', label: 'Created', sortable: true },
  { id: 'updated', label: 'Updated', sortable: true },
];

interface SavedQuery {
  name: string;
  jql: string;
  timestamp: number;
}

const QueryPage: React.FC = () => {
  const [jql, setJql] = useState('');
  const [queryName, setQueryName] = useState('');
  const [savedQueries, setSavedQueries] = useState<SavedQuery[]>([]);
  const [loading, setLoading] = useState(false);
  const [issues, setIssues] = useState<JiraIssue[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [totalResults, setTotalResults] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof JiraIssue | 'slNo' | 'summary' | 'status' | 'priority' | 'assignee' | 'created' | 'updated'>('key');
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>({});
  const [expandedNotes, setExpandedNotes] = useState<Record<string, boolean>>({});
  const [existingNotes, setExistingNotes] = useState<Record<string, string>>({});
  const [scrubNotes, setScrubNotes] = useState<Record<string, string>>({});
  const [submittingNotes, setSubmittingNotes] = useState<Record<string, boolean>>({});
  const [noteErrors, setNoteErrors] = useState<Record<string, string | null>>({});
  
  const navigate = useNavigate();

  // RC Replace the useEffect hook
  useEffect(() => {
    // Check if user is authenticated
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API_URL}/auth/check`, { withCredentials: true });
        if (!response.data.authenticated) {
          navigate('/login');
        }
      } catch (err) {
        navigate('/login');
      }
    };
    
    checkAuth();
  
    // Load saved queries from local storage
    const savedQueriesStr = localStorage.getItem('savedQueries');
    if (savedQueriesStr) {
      const queries = JSON.parse(savedQueriesStr);
      setSavedQueries(queries);
    }
  }, [navigate]);

  // RC Replace the fetchAllIssues function
  const fetchAllIssues = async () => {
    let allIssues: JiraIssue[] = [];
    let startAt = 0;
    const maxResults = 100;
    let total = 0;
  
    try {
      while (true) {
        const response = await axios.post(`${API_URL}/jira`, {
          endpoint: '/rest/api/3/search',
          method: 'POST',
          params: {
            jql: jql.trim(),
            startAt,
            maxResults,
            fields: ['summary', 'status', 'priority', 'assignee', 'created', 'updated'],
            expand: ['renderedFields']
          }
        }, { withCredentials: true });
  
        if (response.data.issues) {
          allIssues = [...allIssues, ...response.data.issues];
          total = response.data.total;
          
          if (allIssues.length >= total || response.data.issues.length < maxResults) {
            break;
          }
          
          startAt += maxResults;
        } else {
          break;
        }
      }
  
      setTotalResults(total);
      return allIssues;
    } catch (err: any) {
      throw err;
    }
  };

  // RC Replace the handleSearch function
  const handleSearch = async () => {
    if (!queryName.trim()) {
      setError('Please provide a name for your query');
      return;
    }
  
    setLoading(true);
    setError(null);
    setPage(0);
    try {
      const allIssues = await fetchAllIssues();
      setIssues(allIssues);
  
      // Save successful query
      const newQuery: SavedQuery = {
        name: queryName.trim(),
        jql: jql.trim(),
        timestamp: Date.now()
      };
  
      const updatedQueries = [newQuery, ...savedQueries]
        .filter((query, index, self) => 
          index === self.findIndex(q => q.name === query.name)
        )
        .slice(0, 5);
  
      setSavedQueries(updatedQueries);
      localStorage.setItem('savedQueries', JSON.stringify(updatedQueries));
    } catch (err: any) {
      console.error('Search error:', err);
      if (err.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
        navigate('/login');
      } else if (err.response?.status === 400) {
        setError(`Invalid query: ${err.response.data.error?.message || 'Please check your JQL syntax'}`);
      } else {
        setError(err.response?.data?.error?.message || 'Failed to search issues');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadSavedQuery = (query: SavedQuery) => {
    setQueryName(query.name);
    setJql(query.jql);
  };

  const handleRequestSort = (property: keyof JiraIssue | 'slNo' | 'summary' | 'status' | 'priority' | 'assignee' | 'created' | 'updated') => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getComparator = () => {
    return (a: JiraIssue, b: JiraIssue) => {
      if (orderBy === 'slNo') {
        return order === 'asc' ? 1 : -1;
      }
      if (orderBy === 'key') {
        return order === 'asc' ? a.key.localeCompare(b.key) : b.key.localeCompare(a.key);
      }
      if (orderBy === 'summary') {
        return order === 'asc' 
          ? a.fields.summary.localeCompare(b.fields.summary)
          : b.fields.summary.localeCompare(a.fields.summary);
      }
      if (orderBy === 'status') {
        return order === 'asc'
          ? a.fields.status.name.localeCompare(b.fields.status.name)
          : b.fields.status.name.localeCompare(a.fields.status.name);
      }
      if (orderBy === 'priority') {
        return order === 'asc'
          ? a.fields.priority.name.localeCompare(b.fields.priority.name)
          : b.fields.priority.name.localeCompare(a.fields.priority.name);
      }
      if (orderBy === 'assignee') {
        const aAssignee = a.fields.assignee?.displayName || 'Unassigned';
        const bAssignee = b.fields.assignee?.displayName || 'Unassigned';
        return order === 'asc'
          ? aAssignee.localeCompare(bAssignee)
          : bAssignee.localeCompare(aAssignee);
      }
      if (orderBy === 'created') {
        return order === 'asc'
          ? new Date(a.fields.created).getTime() - new Date(b.fields.created).getTime()
          : new Date(b.fields.created).getTime() - new Date(a.fields.created).getTime();
      }
      if (orderBy === 'updated') {
        return order === 'asc'
          ? new Date(a.fields.updated).getTime() - new Date(b.fields.updated).getTime()
          : new Date(b.fields.updated).getTime() - new Date(a.fields.updated).getTime();
      }
      return 0;
    };
  };

  const getPriorityColor = (priority: string) => {
    const colors: { [key: string]: string } = {
      'Highest': '#FF0000',
      'High': '#FF4500',
      'Medium': '#FFA500',
      'Low': '#90EE90',
      'Lowest': '#98FB98'
    };
    return colors[priority] || '#808080';
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      'To Do': '#808080',
      'In Progress': '#1E90FF',
      'Done': '#32CD32',
      'Blocked': '#FF0000'
    };
    return colors[status] || '#808080';
  };

  const handleFilterClick = (type: 'priority' | 'status', value: string) => {
    setActiveFilters(prev => {
      const newFilters = { ...prev };
      if (newFilters[type] === value) {
        delete newFilters[type];
      } else {
        newFilters[type] = value;
      }
      return newFilters;
    });
    setPage(0);
  };

  const getFilteredIssues = () => {
    return issues.filter(issue => {
      if (activeFilters.priority && issue.fields.priority.name !== activeFilters.priority) {
        return false;
      }
      if (activeFilters.status && issue.fields.status.name !== activeFilters.status) {
        return false;
      }
      return true;
    });
  };

  // Calculate pagination with filtered results
  const filteredIssues = getFilteredIssues();
  const sortedIssues = [...filteredIssues].sort(getComparator());
  const paginatedIssues = sortedIssues.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleNoteChange = (issueKey: string, value: string) => {
    // Remove the header if it exists
    const headerPattern = new RegExp(`^.*? \\[.*?\\]: `);
    const noteContent = value.replace(headerPattern, '');
    
    setScrubNotes(prev => ({
      ...prev,
      [issueKey]: noteContent
    }));
  };

  // RC Replace the handleSubmitNote function
  const handleSubmitNote = async (issueKey: string): Promise<void> => {
    const note = scrubNotes[issueKey];
    if (!note) {
      setNoteErrors(prev => ({ ...prev, [issueKey]: 'Note cannot be empty' }));
      return;
    }
  
    setSubmittingNotes(prev => ({ ...prev, [issueKey]: true }));
    try {
      const timestamp = new Date().toLocaleString();
      // Use userName from sessionStorage instead of email
      const userName = sessionStorage.getItem('userName') || 'User';
      const formattedNote = `${userName} [${timestamp}]: ${note.trim()}`;
  
      await axios.post(`${API_URL}/jira/scrubnote`, {
        issueKey,
        note: formattedNote
      }, { withCredentials: true });
  
      // Clear the note input and fetch updated notes
      setScrubNotes(prev => ({ ...prev, [issueKey]: '' }));
      setNoteErrors(prev => ({ ...prev, [issueKey]: null }));
      await fetchExistingNotes(issueKey);
      setSuccess(`Note added successfully to ${issueKey}`);
    } catch (error) {
      console.error('Error submitting note:', error);
      setNoteErrors(prev => ({
        ...prev,
        [issueKey]: 'Failed to submit note. Please try again.'
      }));
    } finally {
      setSubmittingNotes(prev => ({ ...prev, [issueKey]: false }));
    }
  };

  // RC Replace the fetchExistingNotes function
  const fetchExistingNotes = async (issueKey: string): Promise<void> => {
    try {
      const response = await axios.get(`${API_URL}/jira/scrubnote/${issueKey}`, {
        withCredentials: true
      });
      
      // Only update state if there's actual content
      if (response.data.content) {
        setExistingNotes((prev: Record<string, string>) => ({
          ...prev,
          [issueKey]: response.data.content
        }));
      }
    } catch (error) {
      console.error('Error fetching existing notes:', error);
    }
  };

  // RC Replace the handleToggleNote function
  const handleToggleNote = async (issueKey: string): Promise<void> => {
    setExpandedNotes(prev => ({
      ...prev,
      [issueKey]: !prev[issueKey]
    }));
  
    if (!expandedNotes[issueKey]) {
      try {
        const response = await axios.get(`${API_URL}/jira/scrubnote/${issueKey}`, {
          withCredentials: true
        });
        
        if (response.data.content) {
          setExistingNotes(prev => ({
            ...prev,
            [issueKey]: response.data.content
          }));
        }
      } catch (error) {
        console.error('Error fetching existing notes:', error);
        setNoteErrors(prev => ({
          ...prev,
          [issueKey]: 'Failed to fetch existing notes'
        }));
      }
    }
  };



  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3, md: 4 } }}>
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Barracuda Bug Scrubber
        </Typography>
        {savedQueries.length > 0 && (
          <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {savedQueries.map((query) => (
              <Chip
                key={query.name}
                label={query.name}
                onClick={() => loadSavedQuery(query)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                    color: 'white'
                  }
                }}
              />
            ))}
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            value={queryName}
            onChange={(e) => setQueryName(e.target.value)}
            placeholder="Query name"
            variant="outlined"
            multiline
            minRows={2}
            maxRows={2}
            sx={{ 
              width: '300px',
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: '2px',
                },
              },
              '& .MuiInputBase-input': {
                '&:focus': {
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                },
              },
            }}
          />
          <TextField
            value={jql}
            onChange={(e) => setJql(e.target.value)}
            placeholder="Enter JQL query..."
            variant="outlined"
            multiline
            minRows={2}
            maxRows={4}
            sx={{ 
              flex: 1,
              '& .MuiInputBase-root': {
                '& textarea': {
                  resize: 'vertical',
                },
              },
            }}
          />
        </Box>
        

        
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={loading || !jql.trim() || !queryName.trim()}
            sx={{ mr: 2 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Search Issues'}
          </Button>
          <Button
            variant="outlined"
            onClick={async () => {
              try {
                await axios.post(`${API_URL}/auth/logout`, {}, { withCredentials: true });
              } catch (err) {
                console.error('Logout error:', err);
              }
              navigate('/login');
            }}
          >
            Logout
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {issues.length > 0 && (
        <>
          <Paper sx={{ p: 2, mb: 2, width: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Search Results Summary
            </Typography>
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Bugs
                </Typography>
                <Typography variant="h4">
                  {filteredIssues.length} / {totalResults}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Priority Distribution
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {Object.entries(
                    issues.reduce((acc, issue) => {
                      const priority = issue.fields.priority.name;
                      acc[priority] = (acc[priority] || 0) + 1;
                      return acc;
                    }, {} as Record<string, number>)
                  ).map(([priority, count]) => (
                    <Chip
                      key={priority}
                      label={`${priority}: ${count}`}
                      size="small"
                      onClick={() => handleFilterClick('priority', priority)}
                      sx={{
                        backgroundColor: getPriorityColor(priority),
                        color: 'white',
                        cursor: 'pointer',
                        opacity: activeFilters.priority && activeFilters.priority !== priority ? 0.5 : 1,
                        '&:hover': {
                          opacity: 0.8
                        }
                      }}
                    />
                  ))}
                </Box>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Status Distribution
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {Object.entries(
                    issues.reduce((acc, issue) => {
                      const status = issue.fields.status.name;
                      acc[status] = (acc[status] || 0) + 1;
                      return acc;
                    }, {} as Record<string, number>)
                  ).map(([status, count]) => (
                    <Chip
                      key={status}
                      label={`${status}: ${count}`}
                      size="small"
                      onClick={() => handleFilterClick('status', status)}
                      sx={{
                        backgroundColor: getStatusColor(status),
                        color: 'white',
                        cursor: 'pointer',
                        opacity: activeFilters.status && activeFilters.status !== status ? 0.5 : 1,
                        '&:hover': {
                          opacity: 0.8
                        }
                      }}
                    />
                  ))}
                </Box>
              </Box>
              {(activeFilters.priority || activeFilters.status) && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Button
                    size="small"
                    onClick={() => setActiveFilters({})}
                    sx={{ ml: 2 }}
                  >
                    Clear Filters
                  </Button>
                </Box>
              )}
            </Box>
          </Paper>
          <Paper sx={{ width: '100%' }}>
            <TableContainer sx={{ maxHeight: 'calc(100vh - 400px)', minHeight: '500px' }}>
              <Table stickyHeader size="medium">
                <TableHead sx={{ backgroundColor: '#1976D2' }}>
                  <TableRow>
                    <TableCell sx={{ 
                      width: '60px', 
                      minWidth: '60px', 
                      textAlign: 'center', 
                      fontWeight: 'bold',
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      backgroundColor: '#1976D2',
                      color: 'white'
                    }}>Scrubber</TableCell>
                    {headCells.map((headCell) => (
                      <TableCell
                        key={headCell.id}
                        sortDirection={orderBy === headCell.id ? order : false}
                        sx={{
                          whiteSpace: 'nowrap',
                          minWidth: headCell.id === 'summary' ? '300px' : 'auto',
                          fontWeight: 'bold',
                          borderRight: '1px solid rgba(224, 224, 224, 1)',
                          backgroundColor: '#1976D2',
                          color: 'white'
                        }}
                      >
                        {headCell.sortable ? (
                          <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={() => handleRequestSort(headCell.id)}
                            sx={{ fontWeight: 'bold' }}
                          >
                            {headCell.label}
                          </TableSortLabel>
                        ) : (
                          headCell.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedIssues.map((issue) => (
                    <React.Fragment key={issue.key}>
                      <TableRow hover>
                        <TableCell sx={{ 
                          width: '60px', 
                          minWidth: '60px', 
                          textAlign: 'center',
                          borderRight: '1px solid rgba(224, 224, 224, 1)'
                        }}>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleNote(issue.key)}
                            sx={{
                              transform: expandedNotes[issue.key] ? 'rotate(45deg)' : 'none',
                              transition: 'transform 0.2s',
                              '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                              },
                            }}
                          >
                            <AddIcon />
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ 
                          width: '100px', 
                          minWidth: '100px',
                          borderRight: '1px solid rgba(224, 224, 224, 1)'
                        }}>
                          <Link
                            href={`https://cuda.atlassian.net/browse/${issue.key}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: 'primary.main',
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            {issue.key}
                          </Link>
                        </TableCell>
                        <TableCell sx={{ 
                          minWidth: '300px',
                          borderRight: '1px solid rgba(224, 224, 224, 1)'
                        }}>
                          {issue.fields.summary}
                        </TableCell>
                        <TableCell sx={{ 
                          whiteSpace: 'nowrap',
                          borderRight: '1px solid rgba(224, 224, 224, 1)'
                        }}>
                          <Chip
                            label={issue.fields.status.name}
                            size="small"
                            sx={{
                              backgroundColor: getStatusColor(issue.fields.status.name),
                              color: 'white',
                              fontWeight: 'bold',
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ 
                          whiteSpace: 'nowrap',
                          borderRight: '1px solid rgba(224, 224, 224, 1)'
                        }}>
                          <Chip
                            label={issue.fields.priority.name}
                            size="small"
                            sx={{
                              backgroundColor: getPriorityColor(issue.fields.priority.name),
                              color: 'white',
                              fontWeight: 'bold',
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ 
                          whiteSpace: 'nowrap',
                          borderRight: '1px solid rgba(224, 224, 224, 1)'
                        }}>
                          {issue.fields.assignee?.displayName || 'Unassigned'}
                        </TableCell>
                        <TableCell sx={{ 
                          whiteSpace: 'nowrap',
                          borderRight: '1px solid rgba(224, 224, 224, 1)'
                        }}>
                          {new Date(issue.fields.created).toLocaleDateString()}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {new Date(issue.fields.updated).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
                          <Collapse in={expandedNotes[issue.key]} timeout="auto" unmountOnExit>
                            <Box sx={{ px: 2, pb: 1, bgcolor: 'background.default' }}>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {existingNotes[issue.key] && (
                                  <Box sx={{ 
                                    mb: 1, 
                                    p: 1, 
                                    backgroundColor: 'grey.50',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'grey.300'
                                  }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                                      Existing Notes:
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{
                                        whiteSpace: 'pre-wrap',
                                        fontStyle: 'italic',
                                        opacity: 0.8,
                                        fontSize: '0.825rem'
                                      }}
                                    >
                                      {/* Use DOMPurify to sanitize content */}
                                      {DOMPurify.sanitize(existingNotes[issue.key])}
                                    </Typography>
                                  </Box>
                                )}
                                  <TextField
                                    multiline
                                    minRows={1}
                                    maxRows={4}
                                    fullWidth
                                    value={scrubNotes[issue.key] || ''}
                                    onChange={(e) => handleNoteChange(issue.key, e.target.value)}
                                    placeholder={`${sessionStorage.getItem('userName') || 'User'} [${new Date().toLocaleString()}]: Enter your new scrubber note here...`}
                                    sx={{
                                      mt: 1,
                                      mb: 1,
                                      '& .MuiInputBase-input': {
                                        lineHeight: '1.2',
                                        fontSize: '0.875rem'
                                      }
                                    }}
                                    error={!!noteErrors[issue.key]}
                                    helperText={noteErrors[issue.key]}
                                  />
                                </Box>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  onClick={() => handleSubmitNote(issue.key)}
                                  disabled={submittingNotes[issue.key] || !scrubNotes[issue.key]?.trim()}
                                  sx={{ minWidth: '80px', height: '32px' }}
                                >
                                  {submittingNotes[issue.key] ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    'Submit'
                                  )}
                                </Button>
                              </Box>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={filteredIssues.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </>
      )}
    </Container>
  );
};

export default QueryPage; 